import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { fireConfetti } from "@/lib/confetti";
import axios from "axios";
import { Trophy, X, Sparkles } from "lucide-react";

export function WeeklyStreakReward({ streak, onDismiss }) {
  const { token } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    // Check if we should show the reward (streak is exactly 7 or multiple of 7)
    if (streak >= 7 && streak % 7 === 0) {
      checkRewardStatus();
    }
  }, [streak]);

  const checkRewardStatus = async () => {
    try {
      const response = await axios.get(`${API}/user/streak-reward/status`, { withCredentials: true });
      
      const lastRewarded = response.data.last_streak_rewarded;
      const lastRewardedStreak = response.data.last_rewarded_streak || 0;
      
      // Show reward if this is a new 7-day milestone
      if (lastRewardedStreak < streak) {
        setShouldShow(true);
        setIsVisible(true);
        // Fire confetti when reward appears
        setTimeout(() => fireConfetti('streak'), 300);
      }
    } catch (error) {
      console.error("Failed to check streak reward status:", error);
      // If endpoint doesn't exist yet, show the reward
      if (streak >= 7 && streak % 7 === 0) {
        setShouldShow(true);
        setIsVisible(true);
        setTimeout(() => fireConfetti('streak'), 300);
      }
    }
  };

  const handleDismiss = async () => {
    setIsVisible(false);
    
    try {
      await axios.post(`${API}/user/streak-reward/claim`, {
        streak_milestone: streak
      }, { withCredentials: true });
    } catch (error) {
      console.error("Failed to claim streak reward:", error);
    }
    
    setTimeout(() => {
      setShouldShow(false);
      onDismiss?.();
    }, 300);
  };

  if (!shouldShow) return null;

  return (
    <div 
      className={`transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
      }`}
    >
      <Card 
        className="relative overflow-hidden cursor-pointer group"
        onClick={handleDismiss}
        data-testid="streak-reward-card"
      >
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#a69373] via-[#d4b87a] to-[#a69373] animate-gradient-x" />
        
        {/* Shimmer overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
        
        <CardContent className="relative p-6 flex items-center gap-4">
          {/* Badge icon with pulse animation */}
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-[#0B2821]/20 flex items-center justify-center animate-pulse-slow">
              <Trophy className="w-8 h-8 text-[#0B2821]" />
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#0B2821] flex items-center justify-center">
              <span className="text-[#a69373] text-xs font-bold">7</span>
            </div>
            {/* Sparkles */}
            <Sparkles className="absolute -top-2 -left-2 w-4 h-4 text-[#0B2821]/60 animate-spin-slow" />
            <Sparkles className="absolute -bottom-1 -right-3 w-3 h-3 text-[#0B2821]/40 animate-spin-slow" style={{ animationDelay: '0.5s' }} />
          </div>
          
          {/* Message */}
          <div className="flex-1">
            <h3 className="text-[#0B2821] font-heading text-lg font-bold mb-1">
              7 days strong
            </h3>
            <p className="text-[#0B2821]/70 text-sm">
              You showed up every day this week.
            </p>
          </div>
          
          {/* Dismiss hint */}
          <div className="flex flex-col items-center text-[#0B2821]/40 group-hover:text-[#0B2821]/60 transition-colors">
            <X className="w-5 h-5" />
            <span className="text-xs">tap</span>
          </div>
        </CardContent>
      </Card>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </div>
  );
}

export default WeeklyStreakReward;
